import React, { useContext, useEffect, useState } from 'react';
import * as Yup from 'yup';
import { useHistory, useLocation } from 'react-router-dom';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

import { AccountContext } from '../../Accounts';
import { DataTypeContext } from '../../DataTypes';
import { API, COGNITO_POOL_ID } from '../../Constants';
import { formatDate } from '../../Utils';
import ProfileForm from './ProfileForm';
import ProfileFormStep2 from './ProfileFormStep2';
import ProfileFormStep3 from './ProfileFormStep3';
import ProfileFormReview from './ProfileFormReview';
import { useCustomTranslator } from '../../utils/customTranslator';

const ProfileInformation = () => {
    const {
        clientProfileId,
        clientId,
        setClientId,
        email,
        phone,
        dob,
        age,
        setLink2FeedClientId,
        firstName,
        lastName,
        setClientProfileId,
        getUserAttributes,
        updateFirstAndLastName,
        consentInfoSigned,
        consentContactEmailSigned,
        consentContactSMSSigned,
        consentContactVoiceSigned,
    } = useContext(AccountContext);

    const { dataTypes } = useContext(DataTypeContext);
    const { useIdWorkflow } = dataTypes;
    const enabledFields = dataTypes.enabledFields || [];
    const requiredFields = dataTypes.requiredFields || [];
    const history = useHistory();
    const location = useLocation();
    const { t } = useTranslation();
    const customT = useCustomTranslator();
    const { executeRecaptcha } = useGoogleReCaptcha();
    const [sessionExpired, setSessionExpired] = useState(false);
    const isPartialProfile =
        (location && location.state !== undefined && location.state.partialProfile) || sessionExpired;
    const isEdit = location && location.state !== undefined && location.state.edit && location.state.edit === true;
    const [isLoading, setLoading] = useState(false);
    const [isUpdateSuccess, setIsUpdateSuccess] = useState(false);
    const [isReview, setIsReview] = useState(false);
    const [currentStep, setCurrentStep] = useState(1);
    const [formData, setFormData] = useState(null);
    const [initialValues, setInitialValues] = useState({
        firstName: firstName || '',
        lastName: lastName || '',
        hhMembers: [],
        expenseTypes: [],
        expenseCount: 0,
        noFixedAddress: false,
        addressDeclinedToAnswer: false,
        ...(enabledFields.includes('income') && { incomeAmount: 0 }),
    });
    const [clientLoading, setClientLoading] = useState(isEdit ? true : false);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [currentStep, isReview]);

    useEffect(() => {
        if (isEdit || formData) {
            return;
        }

        setInitialValues((prev) => {
            const nextFirstName = firstName || prev.firstName || '';
            const nextLastName = lastName || prev.lastName || '';

            if (nextFirstName === prev.firstName && nextLastName === prev.lastName) {
                return prev;
            }

            return {
                ...prev,
                firstName: nextFirstName,
                lastName: nextLastName,
            };
        });
    }, [firstName, lastName, isEdit, formData]);

    useEffect(() => {
        if (!isEdit || !clientId || !executeRecaptcha || !dataTypes.dateFormat) {
            return;
        }

        let isMounted = true;

        const fetchClientDataAndPopulate = async () => {
            try {
                setClientLoading(true);
                const token = await executeRecaptcha('details');
                const res = await fetch(`${API.CLIENT_DETAILS_URL}/${clientId}?token=${token}`);
                const clientData = await res.json();

                if (!isMounted) {
                    return;
                }

                if (clientData && clientData.clientProfileId) {
                    updateFirstAndLastName(clientData.firstName, clientData.lastName);
                    const initialValues = {};
                    initialValues.firstName = clientData.firstName;
                    initialValues.lastName = clientData.lastName;
                    initialValues.dob = clientData.dateOfBirth
                        ? moment(clientData.dateOfBirth, dataTypes.dateFormat.toUpperCase()).toDate()
                        : '';
                    initialValues.age = initialValues.dob
                        ? moment().diff(moment(initialValues.dob), 'years')
                        : '';
                    initialValues.gender = clientData.gender;

                    initialValues.addressLine1 = clientData.addressLine1 ?? '';
                    initialValues.addressLine2 = clientData.addressLine2 ?? '';
                    initialValues.city = clientData.city ?? '';
                    initialValues.county = clientData.county ?? '';
                    initialValues.state = clientData.state ?? '';
                    initialValues.zip = clientData.zipCode ?? '';
                    initialValues.noFixedAddress =
                        clientData.noFixedAddress === 'noFixedAddress' || clientData.noFixedAddress === true;
                    initialValues.addressDeclinedToAnswer =
                        clientData.noFixedAddress === 'addressDeclinedToAnswer';

                    initialValues.employmentType = clientData.employmentType;
                    initialValues.housingType = clientData.housingType;
                    initialValues.educationType = clientData.educationType;
                    initialValues.maritalStatus = clientData.maritalStatus;
                    initialValues.referredBy = clientData.referredBy;
                    initialValues.languageTypes = clientData.languageTypes;

                    initialValues.selfIdentificationType = clientData.selfIdentificationType;
                    initialValues.ethnicityType = clientData.ethnicity;
                    initialValues.dietaryConsiderationTypes = clientData.dietaryConsiderationTypes;
                    initialValues.socialProgramTypes = clientData.socialProgramTypes;
                    initialValues.otherIncomes = Object.keys(clientData.otherIncomes);

                    initialValues.incomeType = clientData.incomeType;
                    initialValues.incomeAmount = clientData.income;
                    initialValues.isHispanicLatino = { true: 'yes', false: 'no' }[clientData.isHispanicLatino];

                    initialValues.hhMembers = [];
                    if (clientData.householdMembers.length > 0) {
                        initialValues.hhMembers = clientData.householdMembers.map((hhm) => {
                            const obj = {};
                            obj.firstName = hhm.firstName;
                            obj.lastName = hhm.lastName;
                            obj.dob = hhm.dateOfBirth
                                ? moment(hhm.dateOfBirth, dataTypes.dateFormat.toUpperCase()).toDate()
                                : '';
                            obj.age = obj.dob
                                ? moment().diff(moment(obj.dob), 'years')
                                : '';
                            obj.hhmEstimatedDOB = hhm.dobEstimated;
                            obj.hhmGender = hhm.gender;
                            obj.hhmRelationship = hhm.relationship;
                            obj.clientProfileId = hhm.clientProfileId;
                            return obj;
                        });
                    }

                    initialValues.expenseCount = clientData.expenseTypes.length;
                    initialValues.expenseTypes = [];
                    for (const [key, value] of Object.entries(clientData.expenseTypes)) {
                        initialValues.expenseTypes.push({ type: key, amount: value });
                    }

                    setInitialValues(initialValues);
                    setClientProfileId(clientData.clientProfileId);
                    setClientLoading(false);
                    return;
                }

                alert(customT('api.error.UNKNOWN'));
                setClientLoading(false);
            } catch (error) {
                console.error('client details exception', error);
                if (isMounted) {
                    alert(customT('api.error.UNKNOWN'));
                    setClientLoading(false);
                }
            }
        };

        fetchClientDataAndPopulate();

        return () => {
            isMounted = false;
        };
    }, [isEdit, clientId, executeRecaptcha, dataTypes.dateFormat]);

    const step1Schema = Yup.object({
        firstName: Yup.string().required('First Name is required'),
        lastName: Yup.string().required('Last Name is required'),
        ...((isPartialProfile || isEdit) &&
            enabledFields.includes('dateOfBirth') && {
                dob: Yup.date().typeError('Please enter a valid date').max(new Date()).label('Date of Birth').required(),
            }),
        ...((isPartialProfile || isEdit) &&
            enabledFields.includes('age') && {
                age: Yup.number().integer('Age must be a whole number').required(),
            }),
        ...(enabledFields.includes('gender') && { gender: Yup.string().required('Gender is required') }),
        ...(requiredFields.includes('maritalStatus') && enabledFields.includes('maritalStatus') && {
            maritalStatus: Yup.string().label('Marital Status').required(),
        }),
        ...(requiredFields.includes('selfIdentificationType') && enabledFields.includes('selfIdentificationType') && {
            selfIdentificationType: Yup.array().of(Yup.string()).label('Self-Identifies As').min(1).required(),
        }),
        ...(requiredFields.includes('ethnicity') && enabledFields.includes('ethnicity') && {
            ethnicityType: Yup.array()
                .of(Yup.string())
                .label(
                    enabledFields.includes('isHispanicLatino')
                        ? t('intake.ethnicities_alternate')
                        : t('intake.ethnicities')
                )
                .min(1)
                .required(),
        }),
        ...(requiredFields.includes('isHispanicLatino') && enabledFields.includes('isHispanicLatino') && {
            isHispanicLatino: Yup.string().label(t('intake.isHispanicLatino')).required(),
        }),
        ...(requiredFields.includes('educationType') && enabledFields.includes('educationType') && {
            educationType: Yup.string().required('Highest Education Level Completed is required'),
        }),
        ...(requiredFields.includes('employmentType') && enabledFields.includes('employmentType') && {
            employmentType: Yup.string().required('Employment Type is required'),
        }),
    });

    const step2Schema = Yup.object({
        hhMembers: Yup.array().of(
            Yup.object().shape({
                firstName: Yup.string().label('First Name').required(),
                lastName: Yup.string().label('Last Name').required(),
                ...(enabledFields.includes('dateOfBirth') && {
                    dob: Yup.date().typeError('Please enter a valid date').label('Date of Birth').max(new Date()).required(),
                }),
                ...(enabledFields.includes('age') && {
                    age: Yup.number().integer('Age must be a whole number').required(),
                }),
                ...(enabledFields.includes('gender') && {
                    hhmGender: Yup.string().label('Gender').required(),
                }),
                ...(enabledFields.includes('relationship') && {
                    hhmRelationship: Yup.string().label('Relationship').required(),
                }),
                hhmEstimatedDOB: Yup.boolean(),
            })
        ),
    });

    const step3Schema = Yup.object({
        noFixedAddress: Yup.boolean(),
        addressDeclinedToAnswer: Yup.boolean(),
        ...(enabledFields.includes('addressLine1') && {
            addressLine1: Yup.string().label('Address Line 1').when(['noFixedAddress', 'addressDeclinedToAnswer'], {
                is: false,
                then: requiredFields.includes('addressLine1') ? Yup.string().required() : Yup.string(),
            }),
        }),
        ...(enabledFields.includes('addressLine2') && {
            addressLine2: Yup.string().label('Address Line 2').when(['noFixedAddress', 'addressDeclinedToAnswer'], {
                is: false,
                then: requiredFields.includes('addressLine2') ? Yup.string().required() : Yup.string(),
            }),
        }),
        ...(enabledFields.includes('city') && {
            city: Yup.string().label('City').when(['noFixedAddress', 'addressDeclinedToAnswer'], {
                is: false,
                then: requiredFields.includes('city') ? Yup.string().required() : Yup.string(),
            }),
        }),
        ...(enabledFields.includes('county') && {
            county: Yup.string().label('County').when(['noFixedAddress', 'addressDeclinedToAnswer'], {
                is: false,
                then: requiredFields.includes('county') ? Yup.string().required() : Yup.string(),
            }),
        }),
        ...(enabledFields.includes('state') && {
            state: Yup.string().label(t('address.state')).when(['noFixedAddress', 'addressDeclinedToAnswer'], {
                is: false,
                then: requiredFields.includes('state') ? Yup.string().required() : Yup.string(),
            }),
        }),
        ...(enabledFields.includes('zipCode') && {
            zip: Yup.string().label(t('address.zipcode')).when(['noFixedAddress', 'addressDeclinedToAnswer'], {
                is: false,
                then: (requiredFields.includes('zipCode') ? Yup.string().required() : Yup.string()).transform((value) =>
                    value.replace(/_/g, '')
                ),
            }),
        }),
        ...(requiredFields.includes('housingType') && enabledFields.includes('housingType') && {
            housingType: Yup.string().label('Housing Type').required(),
        }),
        ...(requiredFields.includes('dietaryConsiderationTypes') && enabledFields.includes('dietaryConsiderationTypes') && {
            dietaryConsiderationTypes: Yup.array().of(Yup.string()).label('Dietary Considerations').min(1).required(),
        }),
        ...(requiredFields.includes('socialProgramTypes') && enabledFields.includes('socialProgramTypes') && {
            socialProgramTypes: Yup.array().of(Yup.string()).label('Social Programs').min(1).required(),
        }),
        ...(requiredFields.includes('languageTypes') && enabledFields.includes('languageTypes') && {
            languageTypes: Yup.array().of(Yup.string()).label('Languages').min(1).required(),
        }),
        ...(requiredFields.includes('incomeType') && enabledFields.includes('incomeType') && {
            incomeType: Yup.string().required('Income Type is required'),
        }),
        ...(requiredFields.includes('income') && enabledFields.includes('income') && {
            incomeAmount: Yup.number().min(0).max(999999).label('Monthly Amount').required(),
        }),
        ...(!requiredFields.includes('income') && enabledFields.includes('income') && {
            incomeAmount: Yup.number().min(0).max(999999).label('Monthly Amount'),
        }),
        ...(requiredFields.includes('referredBy') && enabledFields.includes('referredBy') && {
            referredBy: Yup.string().label('Referred By').required(),
        }),
        ...(requiredFields.includes('expenseTypes') && enabledFields.includes('expenseTypes') && {
            expenseTypes: Yup.array().when('expenseCount', {
                is: (val) => val > 0,
                then: Yup.array()
                    .of(
                        Yup.object().shape({
                            type: Yup.string().label('Type').required(),
                            amount: Yup.number().min(0).max(999999).label('Amount').required(),
                        })
                    )
                    .required(),
            }),
        }),
    });

    const schema = Yup.object({
        firstName: Yup.string().required('First Name is required'),
        lastName: Yup.string().required('Last Name is required'),
        ...((isPartialProfile || isEdit) &&
            enabledFields.includes('dateOfBirth') &&
            { dob: Yup.date().typeError('Please enter a valid date').max(new Date()).label('Date of Birth').required() }),
        ...((isPartialProfile || isEdit) &&
            enabledFields.includes('age') &&
            { age: Yup.number().integer('Age must be a whole number').required() }),
        noFixedAddress: Yup.boolean(),
        addressDeclinedToAnswer: Yup.boolean(),
        ...(enabledFields.includes('addressLine1') && {
            addressLine1: Yup.string()
                .label('Address Line 1')
                .when(['noFixedAddress', 'addressDeclinedToAnswer'], {
                    is: false,
                    then: requiredFields.includes('addressLine1') ? Yup.string().required() : Yup.string()
                }),
        }),
        ...(enabledFields.includes('addressLine2') && {
            addressLine2: Yup.string()
                .label('Address Line 2')
                .when(['noFixedAddress', 'addressDeclinedToAnswer'], {
                    is: false,
                    then: requiredFields.includes('addressLine2') ? Yup.string().required() : Yup.string()
                }),
        }),
        ...(enabledFields.includes('city') && {
            city: Yup.string()
                .label('City')
                .when(['noFixedAddress', 'addressDeclinedToAnswer'], {
                    is: false,
                    then: requiredFields.includes('city') ? Yup.string().required() : Yup.string()
                }),
        }),
        ...(enabledFields.includes('county') && {
            county: Yup.string()
                .label('County')
                .when(['noFixedAddress', 'addressDeclinedToAnswer'], {
                    is: false,
                    then: requiredFields.includes('county') ? Yup.string().required() : Yup.string()
                }),
        }),
        ...(enabledFields.includes('state') && {
            state: Yup.string()
                .label(t('address.state'))
                .when(['noFixedAddress', 'addressDeclinedToAnswer'], {
                    is: false,
                    then: requiredFields.includes('state') ? Yup.string().required() : Yup.string()
                }),
        }),
        ...(enabledFields.includes('zipCode') && {
            zip: Yup.string()
                .label(t('address.zipcode'))
                .when(['noFixedAddress', 'addressDeclinedToAnswer'], {
                    is: false,
                    then: (requiredFields.includes('zipCode') ? Yup.string().required() : Yup.string())
                        .transform(value => value.replace(/_/g, ''))
                }),
        }),
        ...(enabledFields.includes('gender') && { gender: Yup.string().required('Gender is required') }),
        ...(requiredFields.includes('employmentType') && enabledFields.includes('employmentType') && {
            employmentType: Yup.string().required('Employment Type is required'),
        }),
        ...(requiredFields.includes('housingType') && enabledFields.includes('housingType') && { housingType: Yup.string().label('Housing Type').required() }),
        ...(requiredFields.includes('educationType') && enabledFields.includes('educationType') && {
            educationType: Yup.string().required('Highest Education Level Completed is required'),
        }),
        ...(requiredFields.includes('maritalStatus') && enabledFields.includes('maritalStatus') && {
            maritalStatus: Yup.string().label('Marital Status').required(),
        }),
        ...(requiredFields.includes('selfIdentificationType') && enabledFields.includes('selfIdentificationType') && {
            selfIdentificationType: Yup.array().of(Yup.string()).label('Self-Identifies As').min(1).required(),
        }),
        ...(requiredFields.includes('dietaryConsiderationTypes') && enabledFields.includes('dietaryConsiderationTypes') && {
            dietaryConsiderationTypes: Yup.array().of(Yup.string()).label('Dietary Considerations').min(1).required(),
        }),
        ...(requiredFields.includes('socialProgramTypes') && enabledFields.includes('socialProgramTypes') && {
            socialProgramTypes: Yup.array().of(Yup.string()).label('Social Programs').min(1).required(),
        }),
        ...(requiredFields.includes('languageTypes') && enabledFields.includes('languageTypes') && {
            languageTypes: Yup.array().of(Yup.string()).label('Languages').min(1).required(),
        }),
        ...(requiredFields.includes('ethnicity') && enabledFields.includes('ethnicity') && {
            ethnicityType: Yup.array()
                .of(Yup.string())
                .label(
                    enabledFields.includes('isHispanicLatino')
                        ? t('intake.ethnicities_alternate')
                        : t('intake.ethnicities')
                )
                .min(1)
                .required(),
        }),
        ...(requiredFields.includes('isHispanicLatino') && enabledFields.includes('isHispanicLatino') && {
            isHispanicLatino: Yup.string().label(t('intake.isHispanicLatino')).required(),
        }),
        ...(requiredFields.includes('incomeType') && enabledFields.includes('incomeType') && { incomeType: Yup.string().required('Income Type is required') }),
        ...(requiredFields.includes('income') && enabledFields.includes('income') && {
            incomeAmount: Yup.number().min(0).max(999999).label('Monthly Amount').required(),
        }),
        ...(!requiredFields.includes('income') && enabledFields.includes('income') && {
            incomeAmount: Yup.number().min(0).max(999999).label('Monthly Amount'),
        }),
        ...(requiredFields.includes('referredBy') && enabledFields.includes('referredBy') && { referredBy: Yup.string().label('Referred By').required() }),
        hhMembers: Yup.array().of(
            Yup.object().shape({
                firstName: Yup.string().label('First Name').required(),
                lastName: Yup.string().label('Last Name').required(),
                ...(enabledFields.includes('dateOfBirth') && {
                    dob: Yup.date().typeError('Please enter a valid date').label('Date of Birth').max(new Date()).required()
                }),
                ...(enabledFields.includes('age') && {
                    age: Yup.number().integer('Age must be a whole number').required()
                }),
                ...(enabledFields.includes('gender') && { hhmGender: Yup.string().label('Gender').required() }),
                ...(enabledFields.includes('relationship') && {
                    hhmRelationship: Yup.string().label('Relationship').required(),
                }),
                hhmEstimatedDOB: Yup.boolean(),
            })
        ),
        ...(requiredFields.includes('expenseTypes') && enabledFields.includes('expenseTypes') && {
            expenseTypes: Yup.array().when('expenseCount', {
                is: (val) => val > 0,
                then: Yup.array()
                    .of(
                        Yup.object().shape({
                            type: Yup.string().label('Type').required(),
                            amount: Yup.number().min(0).max(999999).label('Amount').required(),
                        })
                    )
                    .required(),
            })
        }),
    });

    const findClient = async (dob) => {
        if (isPartialProfile) {
            const findToken = await executeRecaptcha('details');
            const findPayload = { token: findToken, firstName: firstName, lastName: lastName, dob: formatDate(dob) };
            try {
                const findResponse = await (
                    await fetch(API.FIND_CLIENT_URL, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(findPayload),
                    })
                ).json();

                if (findResponse && findResponse.clientProfileId) {
                    setClientProfileId(findResponse.clientProfileId);

                    if (findResponse.link2feedId !== undefined) {
                        setClientId(findResponse.link2feedId);
                        setLink2FeedClientId((findResponse.link2feedId || '').toString());

                        history.push('/thank-you');
                    }
                }
            } catch (e) {
                console.error('/find exception', e);
                alert(customT('api.error.UNKNOWN'));
            }
        }
    };

    const sendDataToAPI = async () => {
        const data = formData;

        if (enabledFields.includes('age') && data.age) {
            data.dob = moment().subtract(data.age, 'years').toDate();
        }

        if (!isLoading) {
            setLoading(true);
            const token = await executeRecaptcha('details');
            let payload = {
                token: token,
                clientProfileId: clientProfileId,
                ...(isEdit && { firstName: data.firstName }),
                ...(isEdit && { lastName: data.lastName }),
                ...((isEdit || (!isEdit && useIdWorkflow)) && { dateOfBirth: formatDate(data.dob || dob) }),
                ...((isEdit || (!isEdit && useIdWorkflow)) && enabledFields.includes('age') && { age: data.age || age }),
                ...(enabledFields.includes('addressLine1') && { addressLine1: data.addressLine1 }),
                ...(enabledFields.includes('addressLine2') && { addressLine2: data.addressLine2 }),
                ...(enabledFields.includes('city') && { city: data.city }),
                ...(enabledFields.includes('county') && { county: data.county }),
                ...(enabledFields.includes('state') && { state: data.state }),
                ...(enabledFields.includes('zipCode') && { zipCode: data.zip }),
                latitude: data.latitude,
                longitude: data.longitude,
                noFixedAddress: data.noFixedAddress
                    ? 'noFixedAddress'
                    : '' || data.addressDeclinedToAnswer
                    ? 'addressDeclinedToAnswer'
                    : '',
                ...(enabledFields.includes('gender') && { gender: data.gender }),
                ...(enabledFields.includes('income') && { income: data.incomeAmount }),
                ...(enabledFields.includes('incomeType') && { incomeType: data.incomeType }),
                ...(enabledFields.includes('housingType') && { housingType: data.housingType }),
                ...(enabledFields.includes('selfIdentificationType') && {
                    selfIdentificationType: data.selfIdentificationType,
                }),
                ...(enabledFields.includes('educationType') && { educationType: data.educationType }),
                ...(enabledFields.includes('employmentType') && { employmentType: data.employmentType }),
                ...(enabledFields.includes('ethnicity') && { ethnicity: data.ethnicityType }),
                ...(enabledFields.includes('isHispanicLatino') && { isHispanicLatino: data.isHispanicLatino }),
                ...(enabledFields.includes('maritalStatus') && { maritalStatus: data.maritalStatus }),
                shareConsentDate: consentInfoSigned,
                textConsentDate: consentContactSMSSigned,
                emailConsentDate: consentContactEmailSigned,
                voiceConsentDate: consentContactVoiceSigned,
                householdMembers: [],
                ...(enabledFields.includes('otherIncomes') && { otherIncomes: {} }),
                ...(enabledFields.includes('expenseTypes') && { expenseTypes: {} }),
                ...(enabledFields.includes('referredBy') && { referredBy: data.referredBy }),
                ...(enabledFields.includes('socialProgramTypes') && { socialProgramTypes: data.socialProgramTypes }),
                ...(enabledFields.includes('dietaryConsiderationTypes') && {
                    dietaryConsiderationTypes: data.dietaryConsiderationTypes,
                }),
                ...(enabledFields.includes('languageTypes') && { languageTypes: data.languageTypes ?? [] }),
                email: email,
                phoneNumber: phone,
                validationMode: 'case_mgmt',
            };

            data.hhMembers.forEach(function (member, index) {
                if (enabledFields.includes('age') && member.age) {
                    member.dob = moment().subtract(member.age, 'years').toDate();
                }

                payload.householdMembers.push({
                    firstName: member.firstName,
                    lastName: member.lastName,
                    ...({ dateOfBirth: formatDate(member.dob) }),
                    ...(enabledFields.includes('age') && { age: member.age }),
                    ...(enabledFields.includes('gender') && { gender: member.hhmGender }),
                    ...(enabledFields.includes('relationship') && { relationship: member.hhmRelationship }),
                    dobEstimated: member.hhmEstimatedDOB,
                    clientProfileId: member.clientProfileId,
                });
            });

            if (
                enabledFields.includes('otherIncomes') &&
                data.otherIncomes !== undefined &&
                data.otherIncomes.length > 0
            ) {
                data.otherIncomes.forEach(function (type, index) {
                    payload.otherIncomes[type] = 0;
                });
            }

            if (enabledFields.includes('expenseTypes') &&
                data.expenseTypes.length > 0
            ) {
                data.expenseTypes.forEach(function (expense, index) {
                    payload.expenseTypes[expense.type] = expense.amount;
                });
            }

            await fetch(API.NEW_CLIENT_URL, {
                method: isEdit ? 'PATCH' : 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            })
                .then((response) =>
                    response.json().then(async (data) => {
                        if (response.ok && data.link2feedId !== undefined) {
                            setClientId(data.link2feedId);
                            setLink2FeedClientId((data.link2feedId || '').toString());

                            const attributes = await getUserAttributes();

                            const body = {
                                id: attributes.sub,
                                pool_id: COGNITO_POOL_ID,
                            };

                            const url = new URL(API.CNCT_ADMIN_REFETCH_USER_URL);
                            url.search = new URLSearchParams(body).toString();

                            fetch(url, { mode: 'no-cors' });

                            if (isEdit) {
                                setIsUpdateSuccess(true);
                                setLoading(false);
                            } else {
                                history.push('/thank-you');
                            }
                        } else if (data.message !== undefined || data.error !== undefined) {
                            // if session expired, enable partial profile to confirm DOB and get another session
                            const dataError = data.message || data.error;
                            if (dataError === t('session.apiSessionNotFound')) {
                                setSessionExpired(true);
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                            } else alert(dataError);
                            setLoading(false);
                        } else {
                            console.error('client not created');
                            alert(customT('api.error.UNKNOWN'));
                        }
                    })
                )
                .catch((error) => {
                    console.error('clients exception', error);
                    alert(customT('api.error.UNKNOWN'));
                });
        }
    };

    if (isReview) {
        return (
            <ProfileFormReview
                onSubmit={sendDataToAPI}
                onCancel={() => {
                    setIsReview(false);
                    setCurrentStep(3);
                }}
                onDone={() => history.push('/dashboard')}
                schema={schema}
                formData={formData}
                isLoading={isLoading}
                isEdit={isEdit}
                isUpdateSuccess={isUpdateSuccess}
            />
        );
    }

    if (currentStep === 1) {
        return (
            <ProfileForm
                schema={step1Schema}
                onSubmit={(data) => {
                    setFormData(data);
                    setCurrentStep(2);
                }}
                initialValues={formData || initialValues}
                dataTypes={dataTypes}
                isLoading={isLoading}
                isSessionExpired={sessionExpired}
                clientLoading={clientLoading}
                findClient={findClient}
            />
        );
    }

    if (currentStep === 2) {
        return (
            <ProfileFormStep2
                schema={step2Schema}
                onSubmit={(data) => {
                    setFormData(data);
                    setCurrentStep(3);
                }}
                onBack={(data) => {
                    setFormData(data);
                    setCurrentStep(1);
                }}
                initialValues={formData || initialValues}
                dataTypes={dataTypes}
                isLoading={isLoading}
                isEdit={isEdit}
            />
        );
    }

    return (
        <ProfileFormStep3
            schema={step3Schema}
            onSubmit={(data) => {
                setFormData(data);
                setIsReview(true);
            }}
            onBack={(data) => {
                setFormData(data);
                setCurrentStep(2);
            }}
            initialValues={formData || initialValues}
            dataTypes={dataTypes}
            isLoading={isLoading}
            isEdit={isEdit}
        />
    );
};

export default ProfileInformation;
